import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Support chat building blocks shared by the web app and the admin panel (D79):
 *  - <ChatThread>  the conversation (own messages on the right);
 *  - <ChatComposer> text + optional PNG/JPEG image, with the secret check from @custos/core:
 *    recovery phrases / private keys are refused before anything is sent; a possible key that may
 *    be a transaction ID needs an explicit "it's a transaction ID" confirmation.
 * The API enforces the same rules — this is only so people see why at once.
 */
import { detectSecrets, SECRET_KIND_LABEL, secretVerdict } from "@custos/core";
import { ImagePlus, SendHorizontal, ShieldAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "./Button.js";
import { cn } from "./cn.js";
import { Modal } from "./Modal.js";
import { Notice } from "./Notice.js";
import { Spinner } from "./Spinner.js";
export const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
/** The warning shown above every chat. */
export function NeverShareNotice({ className }) {
    return (_jsx(Notice, { tone: "warning", title: "Never share your recovery phrase", ...(className ? { className } : {}), children: "Aurix staff will never ask for your recovery phrase, private keys, password or 2FA code. Messages that look like a recovery phrase or a private key are blocked automatically." }));
}
const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
export function ChatThread({ messages, me, renderImage, emptyText = "No messages yet.", className, }) {
    const end = useRef(null);
    const last = messages.at(-1)?.id;
    useEffect(() => {
        end.current?.scrollIntoView({ block: "end" });
    }, [last]);
    return (_jsxs("div", { className: cn("flex flex-col gap-3", className), "data-testid": "chat-thread", "aria-live": "polite", children: [messages.length === 0 && _jsx("p", { className: "py-8 text-center text-sm text-muted", children: emptyText }), messages.map((m) => {
                if (m.sender === "SYSTEM") {
                    return (_jsx("p", { className: "mx-auto max-w-sm text-center text-xs text-muted", "data-testid": "chat-system", children: m.body }, m.id));
                }
                const mine = m.sender === me;
                return (_jsxs("div", { className: cn("flex flex-col gap-1", mine ? "items-end" : "items-start"), "data-testid": "chat-message", children: [_jsxs("span", { className: "px-2 text-xs text-muted", children: [m.sender === "STAFF" ? (m.senderName ?? "Support") : me === "STAFF" ? "Customer" : "You", " \u00B7", " ", time(m.createdAt)] }), _jsxs("div", { className: cn("max-w-[85%] rounded-[20px] px-4 py-2.5 text-sm break-words whitespace-pre-line sm:max-w-[70%]", mine ? "rounded-br-md bg-fg text-bg" : "rounded-bl-md border border-line bg-soft text-fg"), children: [m.attachmentId && (_jsx("div", { className: "mb-1.5 overflow-hidden rounded-xl", children: renderImage(m.attachmentId) })), m.body] }), m.confirmedTxId && (_jsx("span", { className: "px-2 text-[11px] text-warning", children: "Sender confirmed: this is a transaction ID, not a key" }))] }, m.id));
            }), _jsx("div", { ref: end })] }));
}
function readAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(new Error("Couldn't read the image"));
        reader.readAsDataURL(file);
    });
}
export function ChatComposer({ onSend, disabled = false, placeholder = "Write a message…", }) {
    const [text, setText] = useState("");
    const [image, setImage] = useState(null);
    const [problem, setProblem] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const fileInput = useRef(null);
    useEffect(() => () => {
        if (image)
            URL.revokeObjectURL(image.url);
    }, [image]);
    const verdict = secretVerdict(text);
    const blocked = verdict === "block";
    const blockedWhat = blocked
        ? detectSecrets(text)
            .filter((f) => f.certainty === "definite")
            .map((f) => SECRET_KIND_LABEL[f.kind])
        : [];
    const ambiguousWhat = detectSecrets(text)
        .filter((f) => f.certainty === "ambiguous")
        .map((f) => SECRET_KIND_LABEL[f.kind]);
    const send = async (confirmedTxId) => {
        setSending(true);
        setProblem(null);
        try {
            await onSend({ body: text.trim(), image: image?.base64 ?? null, confirmedTxId });
            setText("");
            setImage(null);
        }
        catch (e) {
            setProblem(e instanceof Error ? e.message : "Couldn't send. Try again.");
        }
        finally {
            setSending(false);
            setConfirmOpen(false);
        }
    };
    const submit = (e) => {
        e.preventDefault();
        if (disabled || sending || blocked || (!text.trim() && !image))
            return;
        if (verdict === "confirm")
            setConfirmOpen(true);
        else
            void send(false);
    };
    const pick = async (file) => {
        setProblem(null);
        if (!file)
            return;
        if (!["image/png", "image/jpeg"].includes(file.type))
            return setProblem("Only PNG or JPEG images.");
        if (file.size > MAX_CHAT_IMAGE_BYTES)
            return setProblem("Images can be up to 5 MB.");
        setImage({ name: file.name, base64: await readAsBase64(file), url: URL.createObjectURL(file) });
    };
    return (_jsxs("form", { onSubmit: submit, className: "flex flex-col gap-2", "data-testid": "chat-composer", children: [blocked && (_jsxs(Notice, { tone: "danger", title: "Not sent", children: ["This looks like ", blockedWhat.join(" and "), ". Never share it with anyone, including Aurix staff. Remove it to send your message."] })), problem && _jsx(Notice, { tone: "danger", children: problem }), image && (_jsxs("div", { className: "flex items-center gap-3 rounded-field border border-line bg-soft p-2", children: [_jsx("img", { src: image.url, alt: "", className: "size-12 rounded-lg object-cover" }), _jsx("span", { className: "min-w-0 flex-1 truncate text-sm text-fg", children: image.name }), _jsx("button", { type: "button", onClick: () => setImage(null), "aria-label": "Remove image", className: "inline-flex size-11 items-center justify-center rounded-full text-muted hover:bg-hover", children: _jsx(X, { className: "size-4", "aria-hidden": true }) })] })), _jsxs("div", { className: "flex items-end gap-2", children: [_jsx("button", { type: "button", onClick: () => fileInput.current?.click(), disabled: disabled, "aria-label": "Add an image", title: "Add an image (PNG or JPEG, up to 5 MB). Never send a photo of your recovery phrase.", className: "inline-flex size-12 shrink-0 items-center justify-center rounded-full border border-line bg-soft text-fg hover:bg-hover disabled:opacity-50", children: _jsx(ImagePlus, { className: "size-5", "aria-hidden": true }) }), _jsx("input", { ref: fileInput, type: "file", accept: "image/png,image/jpeg", className: "sr-only", tabIndex: -1, "aria-label": "Image file", onChange: (e) => {
                            void pick(e.target.files?.[0]);
                            e.target.value = "";
                        } }), _jsx("textarea", { value: text, onChange: (e) => setText(e.target.value), onKeyDown: (e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                e.currentTarget.form?.requestSubmit();
                            }
                        }, rows: 1, maxLength: 2000, disabled: disabled, placeholder: placeholder, "aria-label": "Message", "aria-invalid": blocked, className: "max-h-40 min-h-12 flex-1 resize-none rounded-[24px] border border-line bg-soft px-4 py-3 text-base text-fg placeholder:text-muted/70 focus:border-primary focus:ring-4 focus:ring-primary/20 focus:outline-none aria-[invalid=true]:border-danger sm:text-sm" }), _jsx("button", { type: "submit", "aria-label": "Send", "aria-busy": sending, disabled: disabled || sending || blocked || (!text.trim() && !image), className: "inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-fg text-bg transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-40", children: sending ? (_jsx(Spinner, { className: "size-5", label: "Sending" })) : (_jsx(SendHorizontal, { className: "size-5", "aria-hidden": true })) })] }), _jsx("p", { className: "px-1 text-xs text-muted", children: "Images: PNG or JPEG, up to 5 MB. Never send a photo of your recovery phrase." }), _jsx(Modal, { open: confirmOpen, onOpenChange: setConfirmOpen, title: "Is this a transaction ID?", children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(Notice, { tone: "warning", children: _jsxs("span", { className: "flex items-start gap-2", children: [_jsx(ShieldAlert, { className: "mt-0.5 size-4 shrink-0", "aria-hidden": true }), _jsxs("span", { children: ["Your message contains something that could be ", ambiguousWhat.join(" or "), ". A transaction ID is safe to share. A private key is not: anyone who has it can take your funds."] })] }) }), _jsx(Button, { block: true, onClick: () => void send(true), loading: sending, children: "Yes, it's a transaction ID \u2014 send" }), _jsx(Button, { block: true, variant: "secondary", onClick: () => setConfirmOpen(false), children: "No, don't send" })] }) })] }));
}
/** Loads a chat image with the login token (blob: URL) and frees it again. */
export function ChatImage({ load, alt = "Image sent in the chat", }) {
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let alive = true;
        let made = null;
        load()
            .then((blob) => {
            if (!alive)
                return;
            made = URL.createObjectURL(blob);
            setUrl(made);
        })
            .catch(() => alive && setFailed(true));
        return () => {
            alive = false;
            if (made)
                URL.revokeObjectURL(made);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (failed)
        return _jsx("span", { className: "text-xs text-muted", children: "Image unavailable" });
    if (!url)
        return _jsx("span", { className: "block h-32 w-48 animate-pulse rounded-xl bg-hover" });
    return (_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", children: _jsx("img", { src: url, alt: alt, className: "max-h-72 max-w-full rounded-xl object-contain", "data-testid": "chat-image" }) }));
}
//# sourceMappingURL=Chat.js.map